"use client";

import React, { createContext, useContext, useState, useCallback } from "react";

// ============================================================================
// TYPES
// ============================================================================

export interface DetailNavigationContextType {
  ids: string[];
  index: number;
  entityName: string | null;
  setContext: (ids: string[], index: number, entityName: string) => void;
  clearContext: () => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const DetailNavigationContext = createContext<DetailNavigationContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function DetailNavigationProvider({ children }: { children: React.ReactNode }) {
  const [ids, setIds] = useState<string[]>([]);
  const [index, setIndex] = useState<number>(0);
  const [entityName, setEntityName] = useState<string | null>(null);

  const setContext = useCallback((newIds: string[], newIndex: number, newEntityName: string) => {
    setIds(newIds);
    setIndex(newIndex);
    setEntityName(newEntityName);
  }, []);

  const clearContext = useCallback(() => {
    setIds([]);
    setIndex(0);
    setEntityName(null);
  }, []);

  const value: DetailNavigationContextType = {
    ids,
    index,
    entityName,
    setContext,
    clearContext,
  };

  return (
    <DetailNavigationContext.Provider value={value}>{children}</DetailNavigationContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useDetailNavigation(): DetailNavigationContextType {
  const context = useContext(DetailNavigationContext);
  if (context === undefined) {
    throw new Error("useDetailNavigation must be used within a DetailNavigationProvider");
  }
  return context;
}

/**
 * Optional hook that returns context or null (for optional usage)
 */
export function useDetailNavigationOptional(): DetailNavigationContextType | null {
  return useContext(DetailNavigationContext) ?? null;
}
